import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import {
  UserPlus,
  Mail,
  User,
  Shield,
  Key,
  Loader,
  AlertCircle,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';
import CustomSelect from '../components/common/CustomSelect';

const AdminCreateUser = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: '',
    role: 'editor'
  });

  const adminData = JSON.parse(sessionStorage.getItem('adminData') || '{}');

  useEffect(() => {
    if (adminData.role !== 'super_admin' && adminData.role !== 'dev') {
      navigate('/admin/dashboard');
    }
  }, [adminData.role, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const generatePassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setFormData(prev => ({ ...prev, password }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch('https://api.fsgu.co.za/create-admin.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          created_by: adminData.id
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`Admin user created successfully! Firebase UID: ${data.firebase_uid}`);
        setFormData({
          email: '',
          password: '',
          displayName: '',
          role: 'editor'
        });
      } else {
        setError(data.message || 'Failed to create admin user');
      }
    } catch (err) {
      console.error('Error creating admin:', err);
      setError('Failed to create admin user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${
      isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
    }`}>
      {/* Header */}
      <div className={`sticky top-0 z-30 border-b ${
        isDarkMode
          ? 'bg-fsgu-gray-900/95 border-fsgu-gray-700 backdrop-blur-sm'
          : 'bg-fsgu-silver-50/95 border-fsgu-silver-200 backdrop-blur-sm'
      }`}>
        <div className="container-custom py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Left: Back + Title */}
            <div className="flex items-center gap-3">
              <Link
                to="/admin/users"
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? 'hover:bg-fsgu-gray-800 text-fsgu-silver-300'
                    : 'hover:bg-fsgu-silver-100 text-fsgu-silver-600'
                }`}
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className={`text-3xl sm:text-4xl font-bold font-cursive ${
                  isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                }`}>
                  Create Admin User
                </h1>
                <p className={`text-sm ${
                  isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                }`}>
                  Add a new FSGU staff member
                </p>
              </div>
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/admin/users')}
                className="btn btn-outline"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="container-custom py-6">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Success Message */}
          {success && (
            <div className={`p-4 rounded-xl flex items-start gap-3 ${
              isDarkMode
                ? 'bg-fsgu-green-900/30 border border-fsgu-green-700/50'
                : 'bg-fsgu-green-50 border border-fsgu-green-200'
            }`}>
              <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-600'
              }`} />
              <div>
                <p className={`text-sm font-medium ${
                  isDarkMode ? 'text-fsgu-green-300' : 'text-fsgu-green-700'
                }`}>
                  {success}
                </p>
                <p className={`text-xs mt-1 ${
                  isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-600'
                }`}>
                  Make sure to send the login credentials to the new user.
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className={`p-4 rounded-xl flex items-start gap-3 ${
              isDarkMode
                ? 'bg-fsgu-red-900/30 border border-fsgu-red-700/50'
                : 'bg-fsgu-red-50 border border-fsgu-red-200'
            }`}>
              <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                isDarkMode ? 'text-fsgu-red-400' : 'text-fsgu-red-600'
              }`} />
              <p className={`text-sm ${
                isDarkMode ? 'text-fsgu-red-300' : 'text-fsgu-red-700'
              }`}>
                {error}
              </p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className={`rounded-xl p-6 border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <div className="space-y-6">
              {/* Email */}
              <div>
                <label 
                  htmlFor="email"
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}
                >
                  Email Address <span className="text-fsgu-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className={`w-5 h-5 ${
                      isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                    }`} />
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="user@fsgugolf.co.za"
                    className="input pl-10"
                  />
                </div>
              </div>

              {/* Display Name */}
              <div>
                <label 
                  htmlFor="displayName"
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}
                >
                  Display Name <span className="text-fsgu-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className={`w-5 h-5 ${
                      isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                    }`} />
                  </div>
                  <input
                    type="text"
                    id="displayName"
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleChange}
                    required
                    placeholder="John Smith"
                    className="input pl-10"
                  />
                </div>
              </div>

              {/* Role */}
              <div>
                <label 
                  htmlFor="role"
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}
                >
                  Role <span className="text-fsgu-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                    <Shield className={`w-5 h-5 ${
                      isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                    }`} />
                  </div>
                  <CustomSelect
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    options={[
                      { value: 'editor', label: 'Editor' },
                      { value: 'admin', label: 'Admin' },
                      ...(adminData.role === 'dev' ? [{ value: 'super_admin', label: 'Super Admin' }] : [])
                    ]}
                    placeholder="Select role"
                    className="pl-10"
                  />
                </div>
                <p className={`text-xs mt-2 ${
                  isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'
                }`}>
                  <span className="font-medium">Editor:</span> Content only • <span className="font-medium">Admin:</span> Full access • <span className="font-medium">Super Admin:</span> User management
                </p>
              </div>

              {/* Password */}
              <div>
                <label 
                  htmlFor="password"
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}
                >
                  Password <span className="text-fsgu-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Key className={`w-5 h-5 ${
                      isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                    }`} />
                  </div>
                  <input
                    type="text"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="Minimum 8 characters"
                    className="input pl-10 pr-32"
                  />
                  <button
                    type="button"
                    onClick={generatePassword}
                    className={`absolute inset-y-0 right-0 mr-2 px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                      isDarkMode
                        ? 'text-fsgu-green-400 hover:bg-fsgu-green-500/20'
                        : 'text-fsgu-green-600 hover:bg-fsgu-green-500/10'
                    }`}
                  >
                    Generate
                  </button>
                </div>
                <p className={`text-xs mt-2 ${
                  isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'
                }`}>
                  User will be able to change this after first login
                </p>
              </div>

              {/* Submit Button */}
              <div className={`flex gap-3 pt-6 border-t ${
                isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
              }`}>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary flex-1 inline-flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin mr-2" />
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-5 h-5 mr-2" />
                      <span>Create Admin User</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/admin/users')}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminCreateUser;